import { useTranslation } from 'react-i18next';

/** A device sale_api reports as currently online (unlock/'s 409 payloads). */
export interface ActiveDevice {
  uuid: string;
  device_label: string;
  waiter_name: string;
  last_seen_at: string;
}

interface NameTakeoverPromptProps {
  /** The roster name the waiter just picked — already live elsewhere. */
  waiterName: string;
  /** The device(s) currently signed in under that name. */
  devices: ActiveDevice[];
  busy?: boolean;
  /** Message from a failed confirmation attempt, rendered above the buttons. */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * SC-576 — one active session per waiter account.
 *
 * The second device wins, but not silently: taking the name over signs a
 * colleague's handheld out mid-service, so the waiter confirms first. Shared
 * by both surfaces that can carry a name into unlock/ — NamePicker (picking a
 * name) and PinPad (an idle-lock re-PIN that resends the device's own name).
 */
export default function NameTakeoverPrompt({
  waiterName,
  devices,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: NameTakeoverPromptProps) {
  const { t } = useTranslation();

  return (
    <div
      className="waiter-surface min-h-[100dvh] flex flex-col items-center justify-center gap-4 p-6 bg-cream"
      data-testid="waiter-name-takeover"
    >
      <h2 className="font-display text-2xl text-ink text-center">
        {t('waiter.nameTakeover.title')}
      </h2>
      <p className="font-mono text-sm text-ink/70 text-center max-w-sm">
        {t('waiter.nameTakeover.body', { name: waiterName })}
      </p>
      {devices.length > 0 && (
        <ul className="w-full max-w-sm flex flex-col gap-2">
          {devices.map((device) => (
            <li
              key={device.uuid}
              className="border-2 border-ink bg-paper rounded-btn-sm px-4 py-3 font-mono text-xs text-ink/70"
            >
              {device.device_label || t('waiter.pinPad.unknownDevice')}
            </li>
          ))}
        </ul>
      )}
      <p className="font-mono text-sm text-ink text-center max-w-sm">
        {t('waiter.nameTakeover.warning')}
      </p>
      {error && (
        <p className="text-sm text-red-600" role="alert" data-testid="waiter-name-takeover-error">
          {error}
        </p>
      )}
      <div className="flex gap-2 w-full max-w-sm">
        <button
          type="button"
          data-testid="waiter-name-takeover-cancel"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 bg-paper border-2 border-ink rounded-btn px-4 py-2 font-display disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          data-testid="waiter-name-takeover-confirm"
          onClick={onConfirm}
          disabled={busy}
          className="flex-1 bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn px-4 py-2 font-display disabled:opacity-50"
        >
          {t('waiter.nameTakeover.confirm')}
        </button>
      </div>
    </div>
  );
}
